import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useToast } from '../../../contexts/ToastContext';
import { useAnalyticsSyncActivate } from '../../../hooks/analyticsSync/useAnalyticsSyncActivate';
import { useModal } from '../../../hooks/useModalV2';
import { contentPadding } from '../../../styles/layout';
import { Button } from '../../Button/Button';
import { Description } from '../../Description';
import { Title } from '../styled/fullPageWizard';
import {
  SCHEDULED_EXTRACTION_TIMING_TEXT,
  TIME_FOR_SYNC_TO_COMPLETE_TEXT,
} from './consts';

const Content = styled.div`
  ${contentPadding}
`;

const StyledButton = styled(Button)`
  margin-top: 1.375rem;
`;

const StyledDescription = styled(Description)`
  max-width: 39rem;
`;

type AnalyticsSyncInactiveContentProps = {
  datasetId: string;
  datasetOriginLabel: string;
  organizationId: string;
};

export function AnalyticsSyncInactiveContent({
  datasetId,
  datasetOriginLabel,
  organizationId,
}: AnalyticsSyncInactiveContentProps) {
  const { showUserFriendlyErrorToast, success: showSuccessToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [creator] = useAnalyticsSyncActivate({
    config: { throwOnError: true },
  });
  const { confirm } = useModal();

  const onActivate = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const isConfirmed = await confirm({
        afterConfirmButtonText: 'Activating sync...',
        cancelButtonText: 'Cancel sync',
        confirmButtonText: 'Confirm sync',
        description: `Are you sure you want to sync this ${datasetOriginLabel} to analytics? Some fields will no longer be editable.`,
        onConfirmAsync: async () => {
          await creator({
            inputDatasetId: datasetId,
            organizationId,
          });
        },
        title: 'Confirm Analytics Sync',
      });
      if (isConfirmed) {
        showSuccessToast(
          `Analytics syncing for this ${datasetOriginLabel} was successfully activated. Data is synced ${SCHEDULED_EXTRACTION_TIMING_TEXT} and takes up to ${TIME_FOR_SYNC_TO_COMPLETE_TEXT} to become available for use in Sigma.`,
          { autoHideDuration: 15 * 1000 },
        );
      }
    } catch (err) {
      showUserFriendlyErrorToast(
        err,
        `Unable to activate analytics sync for this ${datasetOriginLabel}.`,
        { dontAutoHide: true },
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    confirm,
    creator,
    datasetId,
    datasetOriginLabel,
    isSaving,
    organizationId,
    showSuccessToast,
    showUserFriendlyErrorToast,
  ]);

  return (
    <Content>
      <Title>Analytics Syncing</Title>
      <StyledDescription>
        {`Once you activate this ${datasetOriginLabel} for analytics, some fields will no longer be editable. `}
      </StyledDescription>
      <StyledButton
        variant="outlined"
        color="primary"
        onClick={onActivate}
        disabled={isSaving}
      >
        Activate sync
      </StyledButton>
    </Content>
  );
}
